import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import {
  Dispatch,
  FC,
  Fragment,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { CreateCampaignForm } from '../../../types/IDiy';
import { LocalizationProvider, DateTimePicker } from '@mui/lab';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { dayJsInstance } from '../../../utils/dayjsHelper';
import { Dayjs } from 'dayjs';
import { Brand, Campaign, CampaignAd, ChangeEventType } from '../../../types';
import { NumericCustomField } from '../../TextField';
import { getSingleCampaignAnalytics } from '../../../services/report';
import { toCurrency } from '../../../utils/numberFormatter';
import { calculateTotalBudget } from '../../../utils/helpers/BudgetHelper';
import { HtmlTooltip } from '../../Tooltip';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';

interface DiyBudgetFormV2Props {
  brand: Brand;
  formValues: CreateCampaignForm;
  setFormValues: Dispatch<SetStateAction<CreateCampaignForm>>;
  timezone: string;
  isEdit: boolean;
  ad?: CampaignAd | null;
  selectedCampaign: Campaign;
  currentMonthlyBudget?: number;
  isRestart?: boolean;
}

const DiyBudgetFormV2: FC<DiyBudgetFormV2Props> = ({
  brand,
  formValues,
  setFormValues,
  timezone,
  isEdit,
  ad = null,
  selectedCampaign,
  currentMonthlyBudget = 0,
  isRestart = false,
}) => {
  const [thruYesterdaySpend, setThruYesterdaySpend] = useState<number>(0);
  const [todaySpend, setTodaySpend] = useState<number>(0);

  const isCampaignLevelBudget =
    formValues?.campaignTemplate?.budgetLevel === 'campaign';

  useEffect(() => {
    (async () => {
      if (isEdit && ad) {
        const startRange = dayJsInstance()
          .tz(timezone)
          .startOf('month')
          .startOf('day');
        const endRange = dayJsInstance().tz(timezone).subtract(1, 'day');

        const thruYesterdaySpend = await getSingleCampaignAnalytics(
          brand?._id,
          startRange,
          endRange,
          'adset',
          ad?.adset_id,
        );

        setThruYesterdaySpend(
          calculateTotalBudget(
            brand,
            parseFloat(thruYesterdaySpend?.data?.spend || '0'),
          ),
        );

        const currentDate = dayJsInstance().tz(timezone);
        const todaySpend = await getSingleCampaignAnalytics(
          brand?._id,
          currentDate,
          currentDate,
          'adset',
          ad?.adset_id,
        );

        setTodaySpend(
          calculateTotalBudget(
            brand,
            parseFloat(todaySpend?.data?.spend || '0'),
          ),
        );
      }
    })();
  }, [isEdit, ad, brand]);

  const handleSetStartDate = (newValue: Dayjs | null) => {
    let temp: CreateCampaignForm = { ...formValues };

    const startDate = newValue.unix();

    temp.adSetTemplate = {
      ...temp.adSetTemplate,
      start_time: startDate,
    };

    setFormValues(temp);
  };

  const handleSetEndDate = (newValue: Dayjs | null) => {
    let temp: CreateCampaignForm = { ...formValues };

    const endDate = newValue.unix();

    temp.adSetTemplate = {
      ...temp.adSetTemplate,
      end_time: endDate,
    };

    setFormValues(temp);
  };

  const handleOnChangeSchedule = (e: ChangeEventType) => {
    let temp: CreateCampaignForm = { ...formValues };

    temp = {
      ...temp,
      adSetTemplate: {
        ...temp?.adSetTemplate,
        daily_budget: null,
        totalBudget: 0,
        futureBudget: 0,
      },
      limited: e.target.value === 'yes',
    };

    setFormValues(temp);
  };

  const handleOnChangeBudgetStrategy = (e: ChangeEventType) => {
    let temp: CreateCampaignForm = { ...formValues };

    temp = {
      ...temp,
      campaignTemplate: {
        ...temp?.campaignTemplate,
        budgetLevel: e.target.value as any,
        totalBudget: 0,
        daily_budget: 0,
        futureBudget: 0,
        totalCharge: 0,
      },
      adSetTemplate: {
        ...temp?.adSetTemplate,
        totalBudget: 0,
        daily_budget: 0,
        futureBudget: 0,
        totalCharge: 0,
      },
    };

    setFormValues(temp);
  };

  const handleOnChangeBudget = (values: any, e: any) => {
    let temp: CreateCampaignForm = { ...formValues };
    let totalCharge: number = 0;
    const isCampaignBudget = temp?.campaignTemplate?.budgetLevel === 'campaign';

    const startDate = dayJsInstance
      .unix(temp?.adSetTemplate?.start_time)
      .tz(timezone);

    const currentDate = dayJsInstance().tz(timezone);

    const isScheduled = startDate > currentDate;
    const endOfMonth = startDate.clone().endOf('month');
    const remainingDays =
      endOfMonth.diff(isScheduled ? startDate : currentDate, 'days') + 1;

    if (!temp?.limited) {
      if (e?.event?.target?.name === 'dailyBudget') {
        const currentBudget = parseFloat(
          (values.floatValue * remainingDays).toFixed(2),
        );

        totalCharge =
          currentBudget > 0 ? currentBudget - currentMonthlyBudget : 0;

        temp = {
          ...temp,
          adSetTemplate: {
            ...temp?.adSetTemplate,
            daily_budget: !isCampaignBudget ? values.floatValue : 0,
            totalBudget: !isCampaignBudget ? currentBudget : 0,
            totalCharge: !isCampaignBudget ? totalCharge : 0,
          },
          campaignTemplate: {
            ...temp?.campaignTemplate,
            daily_budget: isCampaignBudget ? values.floatValue : 0,
            totalBudget: isCampaignBudget ? currentBudget : 0,
            totalCharge: isCampaignBudget ? totalCharge : 0,
          },
        };
      } else if (e?.event?.target?.name === 'totalBudget') {
        const dailyBudget = parseFloat(
          (values.floatValue / remainingDays).toFixed(2),
        );

        totalCharge =
          values.floatValue > 0 ? values.floatValue - currentMonthlyBudget : 0;

        temp = {
          ...temp,
          adSetTemplate: {
            ...temp?.adSetTemplate,
            totalBudget: !isCampaignBudget ? values.floatValue : 0,
            daily_budget: !isCampaignBudget ? dailyBudget : 0,
            totalCharge: !isCampaignBudget ? totalCharge : 0,
          },
          campaignTemplate: {
            ...temp?.campaignTemplate,
            totalBudget: isCampaignBudget ? values.floatValue : 0,
            daily_budget: isCampaignBudget ? dailyBudget : 0,
            totalCharge: isCampaignBudget ? totalCharge : 0,
          },
        };
      }
    } else {
      totalCharge =
        values.floatValue > 0 ? values.floatValue - currentMonthlyBudget : 0;

      temp = {
        ...temp,
        adSetTemplate: {
          ...temp?.adSetTemplate,
          totalBudget: !isCampaignBudget ? values.floatValue : 0,
          totalCharge: !isCampaignBudget ? totalCharge : 0,
        },
        campaignTemplate: {
          ...temp?.campaignTemplate,
          totalBudget: isCampaignBudget ? values.floatValue : 0,
          totalCharge: isCampaignBudget ? totalCharge : 0,
        },
      };
    }

    setFormValues(temp);
  };

  const handleOnUpdateAdBudget = (values: any, e: any) => {
    let temp: CreateCampaignForm = { ...formValues };
    const isCampaignBudget = temp?.campaignTemplate?.budgetLevel === 'campaign';
    const startDate = dayJsInstance
      .unix(temp?.adSetTemplate?.start_time)
      .tz(timezone);
    const startMonth = startDate.clone().month();
    const startYear = startDate.clone().year();
    const currentDate = dayJsInstance().tz(timezone);
    const currentDateMonth = currentDate.clone().month();
    const currentDateYear = currentDate.clone().year();
    const isNewCampaign =
      startMonth === currentDateMonth && startYear === currentDateYear;
    let newPeriodStart: Dayjs | null;
    if (!isNewCampaign) {
      newPeriodStart = currentDate.clone().startOf('month');
    } else {
      newPeriodStart = startDate;
    }

    const endOfMonth = newPeriodStart.clone().endOf('month').endOf('day');
    const remainingDays = endOfMonth.diff(currentDate, 'days') + 1;

    let newBudget: number = 0;
    let totalCharge: number = 0;
    let dailyBudget: number = 0;

    if (!temp?.limited) {
      if (e?.event?.target?.name === 'dailyBudget') {
        dailyBudget = values.floatValue;
        const totalBudget = dailyBudget * remainingDays;
        newBudget = parseInt((totalBudget + thruYesterdaySpend).toFixed(0));
        if (dailyBudget < todaySpend) {
          newBudget = parseInt(
            (newBudget + (todaySpend - dailyBudget)).toFixed(0),
          );
        }

        totalCharge = totalBudget > 0 ? totalBudget - currentMonthlyBudget : 0;

        temp = {
          ...temp,
          adSetTemplate: {
            ...temp?.adSetTemplate,
            daily_budget: !isCampaignBudget ? values.floatValue : 0,
            totalBudget: !isCampaignBudget ? newBudget : 0,
          },
          campaignTemplate: {
            ...temp?.campaignTemplate,
            daily_budget: isCampaignBudget ? values.floatValue : 0,
            totalBudget: isCampaignBudget ? newBudget : 0,
          },
        };
      } else if (e?.event?.target?.name === 'totalBudget') {
        newBudget = values.floatValue;
        const remainingBudget = newBudget - thruYesterdaySpend;
        dailyBudget = parseFloat((remainingBudget / remainingDays).toFixed(2));
        totalCharge = newBudget > 0 ? newBudget - currentMonthlyBudget : 0;

        if (dailyBudget < todaySpend) {
          const thisMonthSpend = thruYesterdaySpend + todaySpend;
          const remainingBudget = newBudget - thisMonthSpend;
          dailyBudget = parseFloat(
            (remainingBudget / (remainingDays - 1)).toFixed(2),
          );
        }

        temp = {
          ...temp,
          adSetTemplate: {
            ...temp?.adSetTemplate,
            totalBudget: !isCampaignBudget ? values.floatValue : 0,
            daily_budget: !isCampaignBudget ? dailyBudget : 0,
          },
          campaignTemplate: {
            ...temp?.campaignTemplate,
            totalBudget: isCampaignBudget ? values.floatValue : 0,
            daily_budget: isCampaignBudget ? dailyBudget : 0,
          },
        };
      }

      temp = {
        ...temp,
        adSetTemplate: {
          ...temp?.adSetTemplate,
          totalCharge: !isCampaignBudget ? totalCharge : 0,
          lifetime_budget: null,
        },
        campaignTemplate: {
          ...temp?.campaignTemplate,
          totalCharge: isCampaignBudget ? totalCharge : 0,
          lifetime_budget: null,
        },
      };
    } else {
      newBudget = values.floatValue;

      temp = {
        ...temp,
        adSetTemplate: {
          ...temp?.adSetTemplate,
          lifetime_budget: !isCampaignBudget ? newBudget : 0,
          daily_budget: null,
          totalBudget: !isCampaignBudget ? newBudget : 0,
          totalCharge: !isCampaignBudget
            ? newBudget > 0
              ? newBudget - currentMonthlyBudget
              : 0
            : 0,
        },
        campaignTemplate: {
          ...temp?.campaignTemplate,
          lifetime_budget: isCampaignBudget ? newBudget : 0,
          daily_budget: null,
          totalBudget: isCampaignBudget ? newBudget : 0,
          totalCharge: isCampaignBudget
            ? newBudget > 0
              ? newBudget - currentMonthlyBudget
              : 0
            : 0,
        },
      };
    }

    setFormValues(temp);
  };

  const calculateNewBudget = (newBudget: number) => {
    return newBudget > 0 ? newBudget - currentMonthlyBudget : 0;
  };

  const getBudgetChangeType = () => {
    if (
      (isCampaignLevelBudget
        ? formValues?.campaignTemplate
        : formValues?.adSetTemplate
      )?.totalBudget < currentMonthlyBudget
    ) {
      return 'Decrease';
    }

    return 'Increase';
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <Grid container spacing={1}>
          {!isEdit && selectedCampaign && isCampaignLevelBudget ? (
            <Grid item xs={12} sx={{ color: 'red', fontWeight: 'bold' }}>
              The budget is set at Campaign Level
            </Grid>
          ) : (
            <Grid item xs={12}>
              Please enter your new desired monthly budget
            </Grid>
          )}

          {isEdit ? (
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight="bold">
                This Month Spend:{' '}
                <Typography variant="body2" component="span">
                  {toCurrency('USD', thruYesterdaySpend + todaySpend)}
                </Typography>
              </Typography>

              <Typography variant="body2" fontWeight="bold">
                This Month Spend (thru yesterday):{' '}
                <Typography variant="body2" component="span">
                  {toCurrency('USD', thruYesterdaySpend)}
                </Typography>
              </Typography>

              <Typography variant="body2" fontWeight="bold">
                Today Spend:{' '}
                <Typography variant="body2" component="span">
                  {toCurrency('USD', todaySpend)}
                </Typography>
              </Typography>
            </Grid>
          ) : null}

          {!isEdit ? (
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="Start Date/Time"
                  value={dayJsInstance
                    .unix(formValues?.adSetTemplate?.start_time)
                    .tz(timezone)}
                  onChange={(newValue) => {
                    handleSetStartDate(newValue);
                  }}
                  minDate={dayJsInstance().tz(timezone)}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        size="small"
                        variant="standard"
                        helperText={
                          <Fragment>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              component="div"
                            >
                              Timezone:
                              <Typography
                                variant="caption"
                                display="inline"
                                ml={1}
                              >
                                {timezone}
                              </Typography>
                            </Typography>
                          </Fragment>
                        }
                        name="start_date"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
          ) : null}

          {!isEdit && !selectedCampaign ? (
            <Grid item xs={12}>
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby="schedule"
                  name="schedule"
                  value={formValues?.limited ? 'yes' : 'no'}
                  onChange={handleOnChangeSchedule}
                  sx={{ flexDirection: 'column' }}
                >
                  <FormControlLabel
                    value="no"
                    control={<Radio size="small" />}
                    label="Run campaign every month"
                  />

                  <FormControlLabel
                    value="yes"
                    control={<Radio size="small" />}
                    label="Stop campaign on"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          ) : null}

          {formValues?.limited ? (
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="End Date/Time"
                  value={dayJsInstance
                    .unix(formValues?.adSetTemplate?.end_time)
                    .tz('US/Eastern')}
                  onChange={(newValue) => handleSetEndDate(newValue)}
                  minDate={dayJsInstance
                    .unix(formValues?.adSetTemplate?.start_time)
                    .tz('US/Eastern')
                    .add(1, 'day')}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        size="small"
                        variant="standard"
                        helperText="Eastern Time"
                        name="end_date"
                      />
                    );
                  }}
                  disabled={!formValues?.adSetTemplate?.start_time}
                />
              </LocalizationProvider>
            </Grid>
          ) : null}

          {!isEdit && !selectedCampaign ? (
            <>
              <Grid
                item
                xs={12}
                sx={{ flexDirection: 'column', display: 'flex' }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Budget Strategy
                </Typography>

                <Typography variant="caption">
                  Choose how to distribute the budget across your campaign.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl>
                  <RadioGroup
                    row
                    aria-labelledby="budgetLevel"
                    name="budgetLevel"
                    value={formValues?.campaignTemplate?.budgetLevel}
                    onChange={handleOnChangeBudgetStrategy}
                    sx={{ flexDirection: 'column' }}
                  >
                    <FormControlLabel
                      value="campaign"
                      control={<Radio size="small" />}
                      label={
                        <Fragment>
                          <Box
                            sx={{
                              flexDirection: 'row',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ marginRight: '5px' }}
                            >
                              Campaign budget
                            </Typography>

                            <HtmlTooltip
                              title={
                                <Fragment>
                                  <Box
                                    sx={{
                                      flexDirection: 'column',
                                      display: 'flex',
                                    }}
                                  >
                                    <Box component="div" mb={1}>
                                      Automatically distribute your budget to
                                      the best opportunities across your
                                      campaign. Also known as Advantage+
                                      campaign budget.
                                    </Box>

                                    <Box
                                      component="a"
                                      href="https://www.facebook.com/business/help/1602924913861363"
                                      target="_blank"
                                    >
                                      About campaign budget
                                    </Box>
                                  </Box>
                                </Fragment>
                              }
                            >
                              <InfoTwoToneIcon
                                sx={{ fontSize: '18px', color: '#096F4D' }}
                              />
                            </HtmlTooltip>
                          </Box>
                        </Fragment>
                      }
                    />

                    <FormControlLabel
                      value="adset"
                      control={<Radio size="small" />}
                      label={
                        <Fragment>
                          <Box
                            sx={{
                              flexDirection: 'row',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ marginRight: '5px' }}
                            >
                              Ad set budget
                            </Typography>

                            <HtmlTooltip
                              title={
                                <Fragment>
                                  Set different bid strategies or budget
                                  schedules for each ad set.
                                </Fragment>
                              }
                            >
                              <InfoTwoToneIcon
                                sx={{ fontSize: '18px', color: '#096F4D' }}
                              />
                            </HtmlTooltip>
                          </Box>
                        </Fragment>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </>
          ) : null}

          {(!isEdit && !selectedCampaign) ||
          (!isEdit && selectedCampaign && !isCampaignLevelBudget) ||
          isEdit ? (
            <>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <NumericCustomField
                      value={
                        (isCampaignLevelBudget
                          ? formValues?.campaignTemplate
                          : formValues?.adSetTemplate
                        )?.totalBudget
                      }
                      onChangeValue={(values: any, e: any) =>
                        isEdit
                          ? handleOnUpdateAdBudget(values, e)
                          : handleOnChangeBudget(values, e)
                      }
                      id="totalBudget"
                      name="totalBudget"
                      label={`${
                        formValues?.limited ? 'Total' : `Current Month's`
                      } Budget`}
                      fullWidth
                      fixedDecimal
                      helperText={
                        !formValues?.limited && brand?.allowSetupPayment
                          ? `Daily Budget: ${toCurrency(
                              'USD',
                              (isCampaignLevelBudget
                                ? formValues?.campaignTemplate
                                : formValues?.adSetTemplate
                              )?.daily_budget || 0,
                            )}`
                          : ''
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <NumericCustomField
                      value={
                        (isCampaignLevelBudget
                          ? formValues?.campaignTemplate
                          : formValues?.adSetTemplate
                        )?.futureBudget
                      }
                      onChangeValue={(values) =>
                        setFormValues({
                          ...formValues,
                          adSetTemplate: {
                            ...formValues?.adSetTemplate,
                            futureBudget: !isCampaignLevelBudget
                              ? values.floatValue
                              : 0,
                          },
                          campaignTemplate: {
                            ...formValues?.campaignTemplate,
                            futureBudget: isCampaignLevelBudget
                              ? values.floatValue
                              : 0,
                          },
                        })
                      }
                      id="futureBudget"
                      name="futureBudget"
                      label="Future Monthly Budget"
                      fullWidth
                      fixedDecimal
                    />
                  </Grid>
                </Grid>
              </Grid>

              {!formValues?.limited && !brand?.allowSetupPayment ? (
                <Grid item xs={12} sm={6}>
                  <NumericCustomField
                    value={
                      (isCampaignLevelBudget
                        ? formValues?.campaignTemplate
                        : formValues?.adSetTemplate
                      )?.daily_budget
                    }
                    onChangeValue={(values: any, e: any) =>
                      isEdit
                        ? handleOnUpdateAdBudget(values, e)
                        : handleOnChangeBudget(values, e)
                    }
                    id="dailyBudget"
                    name="dailyBudget"
                    label="Daily Budget"
                    fullWidth
                    fixedDecimal
                  />
                </Grid>
              ) : null}
            </>
          ) : !isEdit && selectedCampaign && isCampaignLevelBudget ? (
            <>
              <Grid
                item
                xs={12}
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'bold', marginRight: '5px' }}
                >
                  Budget Strategy:
                </Typography>

                <Typography variant="body1">Campaign Level Budget</Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'bold', marginRight: '5px' }}
                >
                  Campaign Schedule:
                </Typography>

                <Typography variant="body1">
                  {formValues?.limited ? 'Limited' : 'On-Going'}
                </Typography>
              </Grid>
            </>
          ) : null}
        </Grid>
      </Grid>

      {isEdit && !isRestart ? (
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Current Monthly Budget
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  {toCurrency('USD', currentMonthlyBudget)}
                </Grid>

                <Grid item xs={6}>
                  {' '}
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    New Monthly Budget
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  {toCurrency(
                    'USD',
                    (isCampaignLevelBudget
                      ? formValues?.campaignTemplate
                      : formValues?.adSetTemplate
                    )?.totalBudget || 0,
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {`Budget ${getBudgetChangeType()}`}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  {toCurrency(
                    'USD',
                    calculateNewBudget(
                      (isCampaignLevelBudget
                        ? formValues?.campaignTemplate
                        : formValues?.adSetTemplate
                      )?.totalBudget || 0,
                    ),
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default DiyBudgetFormV2;
